import React from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import {
  DeleteOutline,
  EditOutlined,
  VisibilityOutlined,
  ToggleOffOutlined,
  ToggleOnOutlined,
} from '@mui/icons-material';
import {
  adminButtonBaseSx,
  adminButtonSizes,
  adminButtonVariants,
} from './adminButtonStyles';

function ActionButton({
  id,
  label,
  icon,
  onClick,
  variant = 'outline',
  disabled = false,
  loading = false,
}) {
  const dangerConfig = adminButtonVariants.danger;
  const activateConfig = adminButtonVariants.outline;
  const deactivateConfig = adminButtonVariants.outline;
  const secondaryConfig = adminButtonVariants.secondary;

  const variantConfig =
    variant === 'danger'
      ? dangerConfig
      : variant === 'activate'
        ? {
            ...activateConfig,
            sx: {
              ...activateConfig.sx,
              borderColor: '#bbf7d0',
              color: '#166534',
              '&:hover': { borderColor: '#86efac', background: '#f0fdf4' },
            },
          }
        : variant === 'deactivate'
          ? {
              ...deactivateConfig,
              sx: {
                ...deactivateConfig.sx,
                borderColor: '#fde68a',
                color: '#b45309',
                '&:hover': { borderColor: '#fcd34d', background: '#fffbeb' },
              },
            }
          : variant === 'secondary'
            ? {
                muiVariant: 'outlined',
                sx: {
                  ...secondaryConfig.sx,
                  borderColor: '#bfdbfe',
                  color: '#1d4ed8',
                  background: '#fff',
                  '&:hover': { borderColor: '#93c5fd', background: '#eff6ff' },
                },
              }
            : adminButtonVariants.outline;

  const button = (
    <Button
      id={id}
      size="small"
      variant={variantConfig.muiVariant}
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={14} /> : icon}
      onClick={onClick}
      aria-label={label}
      sx={{
        ...adminButtonBaseSx,
        ...adminButtonSizes.sm,
        ...variantConfig.sx,
        '&:disabled': { opacity: 0.55 },
      }}
    >
      {label}
    </Button>
  );

  return (
    <Tooltip title={label} arrow placement="top">
      <span>{button}</span>
    </Tooltip>
  );
}

/**
 * Standardized row-level actions for admin listing tables.
 *
 * Actions render in order: View → Edit → Activate/Deactivate → custom → Delete.
 * Omit handlers to hide unsupported actions while preserving layout consistency.
 */
export default function TableRowActions({
  rowId,
  onView,
  onEdit,
  onDelete,
  status,
  disabled = {},
  customActions = [],
  align = 'right',
}) {
  const isActive = status?.isActive ?? false;
  const statusLoading = status?.loading ?? false;
  const statusDisabled = status?.disabled ?? false;

  const containerStyle = {
    display: 'flex',
    gap: '6px',
    justifyContent: align === 'center' ? 'center' : 'flex-end',
    flexWrap: 'wrap',
  };

  return (
    <div style={containerStyle} role="group" aria-label="Row actions">
      {onView && (
        <ActionButton
          id={rowId != null ? `view-row-${rowId}` : undefined}
          label="View"
          icon={<VisibilityOutlined fontSize="small" />}
          onClick={onView}
          variant="outline"
          disabled={disabled.view}
        />
      )}

      {onEdit && (
        <ActionButton
          id={rowId != null ? `edit-row-${rowId}` : undefined}
          label="Edit"
          icon={<EditOutlined fontSize="small" />}
          onClick={onEdit}
          variant="outline"
          disabled={disabled.edit}
        />
      )}

      {status?.onToggle && (
        <ActionButton
          id={rowId != null ? `toggle-row-${rowId}` : undefined}
          label={isActive ? 'Deactivate' : 'Activate'}
          icon={
            statusLoading ? null : isActive ? (
              <ToggleOffOutlined fontSize="small" />
            ) : (
              <ToggleOnOutlined fontSize="small" />
            )
          }
          onClick={status.onToggle}
          variant={isActive ? 'deactivate' : 'activate'}
          disabled={statusDisabled}
          loading={statusLoading}
        />
      )}

      {customActions.map((action) => (
        <ActionButton
          key={action.key}
          id={action.id ?? (rowId != null ? `${action.key}-row-${rowId}` : undefined)}
          label={action.label}
          icon={action.icon}
          onClick={action.onClick}
          variant={action.variant ?? 'outline'}
          disabled={action.disabled}
          loading={action.loading}
        />
      ))}

      {onDelete && (
        <ActionButton
          id={rowId != null ? `delete-row-${rowId}` : undefined}
          label="Delete"
          icon={<DeleteOutline fontSize="small" />}
          onClick={onDelete}
          variant="danger"
          disabled={disabled.delete}
        />
      )}
    </div>
  );
}
